import { Locales } from '@griply/types';
import { NLP } from 'shared/constants';

// Keywords used for date parsing in both English and Dutch. Includes abbreviations.
// The order here doesn't matter as they are compiled into a single regex pattern.
const DATE_KEYWORDS: Record<Locales, string[]> = {
  [Locales.EnUS]: [
    'today',
    'tod',
    'tomorrow',
    'tmw',
    'tmr',
    'next monday',
    'next mon',
    'next tuesday',
    'next tue',
    'next wednesday',
    'next wed',
    'next thursday',
    'next thu',
    'next friday',
    'next fri',
    'next saturday',
    'next sat',
    'next sunday',
    'next sun',
    'monday',
    'mon',
    'tuesday',
    'tue',
    'wednesday',
    'wed',
    'thursday',
    'thu',
    'friday',
    'fri',
    'saturday',
    'sat',
    'sunday',
    'sun',
    'next week',
    'next month',
    'end of month',
    'this weekend',
    'next weekend',
    'next year',
  ],
  [Locales.NlNL]: [
    'vandaag',
    'morgen',
    'volgende week',
    'volgende maand',
    'volgend jaar',
    'dit weekend',
    'volgend weekend',
    'eind van de maand',
    'volgende maandag',
    'volgende dinsdag',
    'volgende woensdag',
    'volgende donderdag',
    'volgende vrijdag',
    'volgende zaterdag',
    'volgende zondag',
    'maandag',
    'dinsdag',
    'woensdag',
    'donderdag',
    'vrijdag',
    'zaterdag',
    'zondag',
  ],
  [Locales.EsES]: [
    'hoy',
    'mañana',
    'la proxima semana',
    'la próxima semana',
    'proxima semana',
    'próxima semana',
    'el proximo mes',
    'el próximo mes',
    'proximo mes',
    'próximo mes',
    'el proximo año',
    'el próximo año',
    'proximo año',
    'próximo año',
    'este fin de semana',
    'el proximo fin de semana',
    'el próximo fin de semana',
    'proximo fin de semana',
    'próximo fin de semana',
    'fin de mes',
    'próximo lunes',
    'próximo martes',
    'próximo miércoles',
    'próximo jueves',
    'próximo viernes',
    'próximo sábado',
    'próximo domingo',
    'proximo lunes',
    'proximo martes',
    'proximo miercoles',
    'proximo jueves',
    'proximo viernes',
    'proximo sabado',
    'proximo domingo',
    'el próximo lunes',
    'el próximo martes',
    'el próximo miércoles',
    'el próximo jueves',
    'el próximo viernes',
    'el próximo sábado',
    'el próximo domingo',
    'el lunes que viene',
    'el martes que viene',
    'el miércoles que viene',
    'el jueves que viene',
    'el viernes que viene',
    'el sábado que viene',
    'el domingo que viene',
    'lunes que viene',
    'martes que viene',
    'miércoles que viene',
    'jueves que viene',
    'viernes que viene',
    'sábado que viene',
    'domingo que viene',
    'lunes',
    'mar',
    'martes',
    'mie',
    'miércoles',
    'jue',
    'jueves',
    'vie',
    'viernes',
    'sab',
    'sábado',
    'dom',
    'domingo',
  ],
  [Locales.DeDE]: [
    'heute',
    'morgen',
    'übermorgen',
    'uebermorgen',
    'nächste woche',
    'naechste woche',
    'nächsten monat',
    'naechsten monat',
    'nächstes jahr',
    'naechstes jahr',
    'dieses wochenende',
    'nächstes wochenende',
    'naechstes wochenende',
    'ende des monats',
    'nächsten montag',
    'naechsten montag',
    'nächsten dienstag',
    'naechsten dienstag',
    'nächsten mittwoch',
    'naechsten mittwoch',
    'nächsten donnerstag',
    'naechsten donnerstag',
    'nächsten freitag',
    'naechsten freitag',
    'nächsten samstag',
    'naechsten samstag',
    'nächsten sonntag',
    'naechsten sonntag',
    'nächsten mo',
    'naechsten mo',
    'nächsten di',
    'naechsten di',
    'nächsten mi',
    'naechsten mi',
    'nächsten do',
    'naechsten do',
    'nächsten fr',
    'naechsten fr',
    'nächsten sa',
    'naechsten sa',
    'nächsten so',
    'naechsten so',
    'montag',
    'mo',
    'dienstag',
    'di',
    'mittwoch',
    'mi',
    'donnerstag',
    'do',
    'freitag',
    'fr',
    'samstag',
    'sa',
    'sonntag',
    'so',
  ],
};

// Generates a comprehensive regex pattern for all supported date formats.
export const getAllDatePatterns = (language: Locales) => {
  const dateKeywords = DATE_KEYWORDS[language]
    .sort((a, b) => b.length - a.length)
    .join('|');
  const monthNames = Object.keys(NLP.MONTHS[language])
    .sort((a, b) => b.length - a.length)
    .join('|');
  const ordinals = NLP.rruleKeywords[language].ordinals; // Use the correct language's ordinals

  // Build the list of all possible date patterns
  const patterns = [
    `(?:${monthNames}) \\d{1,2} \\d{4}`, // mmm dd yyyy
    `\\d{1,2} (?:${monthNames}) \\d{4}`, // dd mmm yyyy
    `\\d{1,2} (?:${monthNames})`, // dd mmm
    `(?:${monthNames}) \\d{1,2}`, // mmm dd
    `\\d{1,2}${ordinals}`, // 27th, 27., 27e, etc.
    `\\d{1,2}[\\/\\.]\\d{1,2}(?:[\\/\\.]\\d{2,4})?`, // dd/mm/yyyy or dd.mm.yyyy
    `\\d{4}-\\d{1,2}-\\d{1,2}`, // yyyy-mm-dd
    `\\b(?:${dateKeywords})\\b`, // today, tomorrow, etc.
  ];

  // Add the specific Spanish pattern for "el [number]"
  if (language === Locales.EsES) {
    patterns.push(`el\\s\\d{1,2}`);
  }

  return `(?:${patterns.join('|')})`;
};
